import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSessionIdentityChange } from 'contexts/ui/useSessionIdentityChange';

import {
  SESSION_CHANGED_COLOR,
  SessionChangedMessage,
} from './SessionChangedMessage';

export const SESSION_CHANGED_NOTIFICATION_ID = 'session-changed';

/**
 * Warns a Compete 2.0 tab that it shows stale data because another tab impersonated, switched
 * company or signed out. The warning stays until the user reloads or chooses to reload later.
 * After "reload later" it does not come back in this tab.
 *
 * Compete 2.0 pages have no AppUIProvider, so this is the Mantine counterpart of
 * ImpersonationGuardNotifier and shares its detection through useSessionIdentityChange.
 */
export function SessionChangedNotifier(): null {
  const { t } = useTranslation(['Session']);
  const dismissedRef = useRef<boolean>(false);

  const handleReload = useCallback((): void => {
    window.location.reload();
  }, []);

  const handleDismiss = useCallback((): void => {
    dismissedRef.current = true;
    notifications.hide(SESSION_CHANGED_NOTIFICATION_ID);
  }, []);

  useSessionIdentityChange(() => {
    if (dismissedRef.current) {
      return;
    }

    // Mantine ignores a show() whose id is already on screen, so focusing the tab again
    // does not stack a second warning.
    notifications.show({
      id: SESSION_CHANGED_NOTIFICATION_ID,
      title: t('Session:identityGuard.title'),
      color: SESSION_CHANGED_COLOR,
      message: (
        <SessionChangedMessage
          onReload={handleReload}
          onDismiss={handleDismiss}
        />
      ),
      autoClose: false,
      withCloseButton: false,
    });
  });

  useEffect(() => {
    return () => {
      notifications.hide(SESSION_CHANGED_NOTIFICATION_ID);
    };
  }, []);

  return null;
}
